import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Volume2,
  VolumeX,
  Sparkles,
  MapPin,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { HERO_SLIDES } from "@/components/heroSlidesData";

/**
 * HeroDesktop — Cabecera inmersiva a pantalla completa con carrusel de vídeo continuo.
 * Alterna de forma secuencial y fluida entre los 3 clips de vídeo de Brickshare.
 */
export const HeroDesktop = () => {
  const { openAuthModal } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const currentSlide = HERO_SLIDES[activeIndex];

  // Cambio de diapositiva
  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index);
    setProgress(0);
  }, []);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    setProgress(0);
  }, []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
    setProgress(0);
  }, []);

  // Sincronización del vídeo activo
  useEffect(() => {
    videoRefs.current.forEach((videoEl, idx) => {
      if (!videoEl) return;
      if (idx === activeIndex) {
        videoEl.currentTime = 0;
        const playPromise = videoEl.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Manejo de restricciones de autoplay si fuera necesario
          });
        }
      } else {
        videoEl.pause();
      }
    });
    setProgress(0);
  }, [activeIndex]);

  // Manejo de audio mute/unmute
  const toggleMute = () => {
    setIsMuted((prev) => {
      const next = !prev;
      videoRefs.current.forEach((v) => {
        if (v) v.muted = next;
      });
      return next;
    });
  };

  const scrollToNextSection = () => {
    const nextSection = document.querySelector("[data-section='value-pillars']") ||
      document.querySelector("main > section:nth-of-type(2)");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-section="hero"
      className="relative w-full h-[calc(100vh-5rem)] min-h-[640px] max-h-[1080px] bg-stone-950 text-white overflow-hidden flex flex-col justify-between select-none"
    >
      {/* 1. Capa de vídeos continuos con fundido cruzado (Crossfade) */}
      <div className="absolute inset-0 z-0">
        {HERO_SLIDES.map((slide, index) => {
          const isActive = index === activeIndex;
          return (
            <video
              key={slide.id}
              ref={(el) => {
                videoRefs.current[index] = el;
              }}
              src={slide.videoSrc}
              poster={slide.posterSrc}
              muted={isMuted}
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              onEnded={handleNext}
              onTimeUpdate={(e) => {
                if (isActive) {
                  const v = e.currentTarget;
                  if (v.duration) {
                    setProgress((v.currentTime / v.duration) * 100);
                  }
                }
              }}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                isActive ? "opacity-100 z-10" : "opacity-0 pointer-events-none z-0"
              }`}
            />
          );
        })}

        {/* Gradientes cinematográficos para máxima legibilidad y estética premium */}
        <div className="absolute inset-0 z-20 bg-gradient-to-r from-stone-950/95 via-stone-950/70 to-stone-950/30" />
        <div className="absolute inset-0 z-20 bg-gradient-to-t from-stone-950/95 via-transparent to-stone-950/40" />
      </div>

      {/* 2. Botón flotante para activar/silenciar sonido */}
      <div className="absolute top-6 right-8 z-30 flex items-center gap-3">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? "Activar audio" : "Silenciar audio"}
          className="group flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-stone-900/70 hover:bg-stone-800/90 text-stone-200 border border-white/10 backdrop-blur-md transition-all shadow-lg text-xs font-medium cursor-pointer"
        >
          {isMuted ? (
            <>
              <VolumeX className="h-4 w-4 text-stone-400 group-hover:text-white" />
              <span>Activar sonido</span>
            </>
          ) : (
            <>
              <Volume2 className="h-4 w-4 text-primary animate-pulse" />
              <span className="text-primary font-semibold">Sonido activo</span>
            </>
          )}
        </button>
      </div>

      {/* 3. Contenido Central: Textos dinámicos y CTAs */}
      <div className="container mx-auto px-6 lg:px-12 relative z-30 pt-10 lg:pt-14 flex-1 flex flex-col justify-center">
        <div className="max-w-3xl">
          {/* Badge superior de proximidad y categoría */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-semibold text-stone-200 mb-6 shadow-sm">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span>Marketplace LEGO® local</span>
            <span className="w-1 h-1 rounded-full bg-stone-400" />
            <span className="text-primary flex items-center gap-1 font-bold">
              <Sparkles className="h-3 w-3" />
              {currentSlide.tag}
            </span>
          </div>

          {/* Bloque con transición fluida de textos por diapositiva */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="space-y-5"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-display font-extrabold tracking-tight text-white leading-[1.08] drop-shadow-sm">
                {currentSlide.titlePart1}
                <span className="text-primary drop-shadow-md">
                  {currentSlide.titleHighlight}
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-stone-200 leading-relaxed max-w-2xl font-light">
                {currentSlide.description}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Botones de acción principales */}
          <div className="flex flex-wrap items-center gap-4 pt-8">
            <Button
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-base font-bold shadow-xl hover:shadow-primary/20 transition-all cursor-pointer group"
              asChild
            >
              <Link to="/catalogo">
                Explorar sets en tu ciudad
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="rounded-full border-white/20 bg-stone-900/60 hover:bg-stone-800/90 text-white px-7 h-14 text-base font-semibold backdrop-blur-md transition-colors cursor-pointer"
              onClick={() => openAuthModal("signup")}
            >
              Publicar mis sets
            </Button>
          </div>

          {/* Micro-píldoras de confianza y tranquilidad */}
          <div className="flex flex-wrap items-center gap-6 pt-7 text-xs sm:text-sm text-stone-300">
            <span className="inline-flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              Entrega en mano gratis o envío asegurado
            </span>
            <span className="inline-flex items-center gap-2 font-medium">
              <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" />
              Fianza opcional, perfiles verificados y fotos reales
            </span>
          </div>
        </div>
      </div>

      {/* 4. Barra Inferior: Línea de tiempo segmentada interactiva y controles */}
      <div className="container mx-auto px-6 lg:px-12 relative z-30 pb-6 pt-4 border-t border-white/10 bg-gradient-to-t from-stone-950/90 to-transparent">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Segmentos de progreso tipo Stories */}
          <div className="grid grid-cols-3 gap-4 w-full md:max-w-2xl">
            {HERO_SLIDES.map((slide, idx) => {
              const isCurrent = idx === activeIndex;
              const isPassed = idx < activeIndex;

              return (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goToSlide(idx)}
                  className="group text-left cursor-pointer focus:outline-none"
                >
                  {/* Barra de progreso */}
                  <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all ease-linear ${
                        isCurrent
                          ? "bg-primary"
                          : isPassed
                          ? "bg-white/80"
                          : "bg-transparent group-hover:bg-white/40"
                      }`}
                      style={{
                        width: isCurrent ? `${progress}%` : isPassed ? "100%" : "0%",
                      }}
                    />
                  </div>

                  {/* Etiquetas de paso */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-bold transition-colors ${
                        isCurrent
                          ? "text-primary"
                          : "text-stone-400 group-hover:text-stone-200"
                      }`}
                    >
                      {slide.stepNumber}. {slide.stepLabel}
                    </span>
                    <span className="text-[10px] text-stone-400 hidden sm:inline">
                      {isCurrent ? "En curso" : ""}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Flechas de navegación y sugerencia de scroll */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 bg-stone-900/60 p-1 rounded-full border border-white/10 backdrop-blur-md">
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Vídeo anterior"
                className="p-1.5 rounded-full hover:bg-white/10 text-stone-300 hover:text-white transition-colors cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                aria-label="Siguiente vídeo"
                className="p-1.5 rounded-full hover:bg-white/10 text-stone-300 hover:text-white transition-colors cursor-pointer"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <button
              type="button"
              onClick={scrollToNextSection}
              className="hidden lg:flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-200 transition-colors cursor-pointer py-1 px-2"
            >
              <span>Descubre más</span>
              <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroDesktop;
